import { useRef, useState } from "react";
import { MAX_PAGE_SIZE } from "@miplatform/constants";
import { groupBy } from "@miplatform/utils";
import { useSeries, useSeriesGroup } from "@mibuildweb/hooks";

interface ISeriesCounts {
  seriesGroupCount: number;
  seriesCount: number;
}

export const useSeriesCountsViewModel = () => {
  const [countsByAccountId, setCountsByAccountId] = useState<
    Record<string, ISeriesCounts>
  >({});

  const latestRequestIdRef = useRef(0);

  const { getSeries } = useSeries();
  const { getSeriesGroups } = useSeriesGroup();

  const getSeriesCounts = async (accountIds: string[]) => {
    const requestId = ++latestRequestIdRef.current;

    if (!accountIds.length) {
      setCountsByAccountId({});
      return;
    }

    const [seriesResponse, seriesGroupResponse] = await Promise.all([
      getSeries({
        filters: { accountId: { in: accountIds } },
        top: MAX_PAGE_SIZE,
      }),
      getSeriesGroups({
        filters: { accountId: { in: accountIds } },
        top: MAX_PAGE_SIZE,
      }),
    ]);

    if (requestId !== latestRequestIdRef.current) {
      // A newer request has since been issued; discard this stale result.
      return;
    }

    const seriesByAccountId = groupBy(seriesResponse?.data ?? [], "accountId");
    const seriesGroupsByAccountId = groupBy(
      seriesGroupResponse?.data ?? [],
      "accountId"
    );

    const counts: Record<string, ISeriesCounts> = Object.fromEntries(
      accountIds.map((accountId) => [
        accountId,
        {
          seriesCount: (seriesByAccountId[accountId] ?? []).length,
          seriesGroupCount: (seriesGroupsByAccountId[accountId] ?? []).length,
        },
      ])
    );

    setCountsByAccountId(counts);
  };

  return {
    countsByAccountId,
    getSeriesCounts,
  };
};
